import { calculateExponentialProgress } from './exponentialProgress';

/** Standard backend checkpoint ladder (percent). */
export const JOB_PROGRESS_CHECKPOINTS = [25, 50, 75, 99] as const;

export const JOB_PROGRESS_ACTIVE_MAX_PERCENT = 99;

export interface JobProgressCheckpoint {
  floorPercent: number;
  ceilingPercent: number;
  segmentStartedAtMs: number;
  expectedDurationSeconds: number;
}

export function clampActiveJobProgressPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(JOB_PROGRESS_ACTIVE_MAX_PERCENT, value));
}

/** Next visual ceiling after a backend checkpoint (e.g. 25 → 50). */
export function inferNextJobProgressCeiling(floorPercent: number): number {
  const floor = clampActiveJobProgressPercent(floorPercent);
  for (const checkpoint of JOB_PROGRESS_CHECKPOINTS) {
    if (checkpoint > floor) return checkpoint;
  }
  return JOB_PROGRESS_ACTIVE_MAX_PERCENT;
}

export function createJobProgressCheckpoint(
  floorPercent: number,
  expectedDurationSeconds: number,
  nowMs: number = Date.now()
): JobProgressCheckpoint {
  const floor = clampActiveJobProgressPercent(floorPercent);
  return {
    floorPercent: floor,
    ceilingPercent: inferNextJobProgressCeiling(floor),
    segmentStartedAtMs: nowMs,
    expectedDurationSeconds: Math.max(1, expectedDurationSeconds),
  };
}

/**
 * Visual estimate between backend checkpoints:
 * floor + (ceiling - floor) * (1 - exp(-elapsed / expectedDuration))
 */
export function estimateJobProgressPercent(
  checkpoint: JobProgressCheckpoint,
  nowMs: number = Date.now()
): number {
  const elapsedSeconds = Math.max(0, (nowMs - checkpoint.segmentStartedAtMs) / 1000);
  const stageProgress = calculateExponentialProgress(elapsedSeconds, checkpoint.expectedDurationSeconds);
  const span = checkpoint.ceilingPercent - checkpoint.floorPercent;
  if (span <= 0) return clampActiveJobProgressPercent(checkpoint.floorPercent);

  const estimated = checkpoint.floorPercent + stageProgress * span;
  return clampActiveJobProgressPercent(Math.max(checkpoint.floorPercent, estimated));
}

export interface SyncJobProgressCheckpointResult {
  checkpoint: JobProgressCheckpoint;
  displayedPercent: number;
  /** True when backend advanced to a new floor and the segment timer restarted. */
  synced: boolean;
}

/**
 * Sync to backend checkpoint. Restarts the exponential segment only when floor increases.
 */
export function syncJobProgressCheckpoint(
  backendPercent: number | string | null | undefined,
  prev: JobProgressCheckpoint | null,
  expectedDurationSeconds: number,
  nowMs: number = Date.now()
): SyncJobProgressCheckpointResult | null {
  if (backendPercent == null) return null;
  if (typeof backendPercent === 'string' && backendPercent.trim() === '') return null;

  const floor = clampActiveJobProgressPercent(
    typeof backendPercent === 'string' ? parseFloat(backendPercent) : Number(backendPercent)
  );
  if (!Number.isFinite(floor)) return null;

  if (!prev || floor > prev.floorPercent) {
    const checkpoint = createJobProgressCheckpoint(floor, expectedDurationSeconds, nowMs);
    return { checkpoint, displayedPercent: floor, synced: true };
  }

  return {
    checkpoint: prev,
    displayedPercent: estimateJobProgressPercent(prev, nowMs),
    synced: false,
  };
}
